// About, privacy, disclaimer and terms: one Markdown document per language (about.<locale>.md), split into
// sections with stable ids so the side nav and deep links such as /about#terms resolve in every language.
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Header } from '@/components/Header';
import { useT } from '@/i18n';
import { Markdown } from '@/lib/markdown';
import type { Locale } from '@/types';
import en from './about.en.md?raw';
import zh from './about.zh-CN.md?raw';
import { splitSections } from './sections';
import s from './about.module.css';

// fr-CA falls back to English like the rest of the UI (translation is scheduled with the catalog).
const DOCS: Partial<Record<Locale, string>> = { 'zh-CN': zh, en };

export function AboutView() {
  const { t, locale } = useT();
  const location = useLocation();
  const doc = useMemo(() => splitSections(DOCS[locale] ?? en), [locale]);

  // A router navigation to /about#privacy does not scroll like a native hash link would; do it once the
  // sections are in the DOM, and again whenever the hash changes while the page is open.
  useEffect(() => {
    const id = location.hash.slice(1);
    if (!id) return;
    document.getElementById(id)?.scrollIntoView({ block: 'start' });
  }, [location.hash, locale]);

  return (
    <div className="page">
      <Header crumbs={[{ label: t('header.list'), to: '/' }]} title={t('about.title')} showCurrency={false} />

      <section className={s.layout}>
        <nav className={s.nav} aria-label={t('about.title')}>
          {doc.sections.map((sec) => <a key={sec.id} href={`#${sec.id}`}>{sec.title}</a>)}
        </nav>

        <div>
          {doc.intro && <p className={`muted ${s.intro}`}>{doc.intro}</p>}
          {doc.sections.map((sec) => (
            <section key={sec.id} id={sec.id} className={s.sec}>
              <h2 className={`serif ${s.h2}`}>{sec.title}</h2>
              <div className={s.body}><Markdown text={sec.body} /></div>
            </section>
          ))}
          <p className={`muted ${s.foot}`}>{t('about.footNote')}</p>
        </div>
      </section>
    </div>
  );
}
